/* eslint-disable react/no-unused-prop-types */
import React, {
  useContext, useState, useEffect, useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Warning } from '@one-thd/sui-icons';
import { Typography, Link } from '@one-thd/sui-atomic-components';
import KeyProductFeatureContents from './KeyProductFeatureContents';
import dataModel from '../dataModel';
import { TileGridSkeletonPlaceholder } from './TileGridSkeletonPlaceholder';
import { getKpfFromSpecifications, filterAttributeDuplicates, returnObject, addKpfIfPresent } from '../helper';

const processFeatures = ({
  keyProductFeatures, product, minfeatures, channel, firstKPFName
}) => {
  const features = keyProductFeatures?.keyProductFeaturesItems?.[0]?.features || [];
  let featuresAttr = filterAttributeDuplicates({ features, product });

  if (featuresAttr.length < minfeatures && channel === 'mobile') {
    featuresAttr = getKpfFromSpecifications(product).map((spec) => ({
      name: spec.specName,
      value: spec.specValue
    }));
  }

  return addKpfIfPresent(featuresAttr, firstKPFName);
};

const NoSpecsAvailable = ({ canonicalUrl }) => (
  <div className="sui-bg-strong">
    <div className="sui-flex sui-flex-col sui-items-center sui-text-center sui-py-8">
      <Warning />
      <span className="sui-pt-2">
        <Typography variant="body-sm" weight="bold">No specs available</Typography>
      </span>
      <span className="sui-py-3">
        <Typography variant="body-xs">Specifications for this product are currently unavailable</Typography>
      </span>
      {canonicalUrl && (
        <Link href={canonicalUrl} decoration="underline" align="center" variant="body-xs">
          View full product details
        </Link>
      )}
    </div>
  </div>
);

NoSpecsAvailable.propTypes = {
  canonicalUrl: PropTypes.string
};

NoSpecsAvailable.defaultProps = {
  canonicalUrl: ''
};

const KeyProductFeatures = (props) => {

  const {
    hideImage,
    hideForProductTypes,
    itemId,
    minfeatures,
    maxfeatures,
    displaySimilarLinks,
    twoColumnGrid,
    oneColumnGrid,
    simple,
    forceMobile,
    firstKPFName,
    isConfigurableProduct,
    inverseText,
    responsiveTileGrid
  } = props;
  const { channel } = useContext(ExperienceContext);

  const [kpfCachedData, setkpfCachedData] = useState({
    featuresAttr: [],
    url: null,
    canonicalUrl: null,
    shouldHide: false,
    itemId: null
  });

  const cachedItemId = useRef(null);

  const { data, loading } = useDataModel('product', {
    variables: { itemId }
  });

  const { product, keyProductFeatures, isMatch, hasSpecs } = useMemo(() => {
    const productData = returnObject(data);
    const kpf = returnObject(productData.product);
    const productItemId = productData.product?.identifiers?.itemId;
    const matchesCurrentItem = productItemId === itemId;
    const hasSpecsData = matchesCurrentItem
      && kpf.keyProductFeatures?.keyProductFeaturesItems?.length > 0;

    return {
      product: productData.product,
      keyProductFeatures: kpf.keyProductFeatures,
      isMatch: matchesCurrentItem,
      hasSpecs: hasSpecsData
    };
  }, [data, itemId]);

  useEffect(() => {
    // Skip if no fresh data, configurable product, or current item id already cached
    if (!isMatch || isConfigurableProduct || cachedItemId.current === itemId) {
      return;
    }

    const featuresAttr = hasSpecs
      ? processFeatures({
        keyProductFeatures,
        product,
        minfeatures,
        channel,
        firstKPFName
      })
      : [];

    cachedItemId.current = itemId;

    setkpfCachedData({
      featuresAttr,
      url: hideImage ? null : product.media?.image?.url,
      canonicalUrl: product.identifiers?.canonicalUrl,
      shouldHide: hideForProductTypes?.includes(product?.info?.productSubType?.name),
      itemId
    });

  }, [isMatch, hasSpecs, itemId, isConfigurableProduct]);

  const isWaitingForData = loading || !data || !isMatch;
  const hasCachedData = kpfCachedData.itemId === itemId;

  if (isWaitingForData && !hasCachedData) {
    return responsiveTileGrid ? <TileGridSkeletonPlaceholder /> : null;
  }

  // First mount - data is loading for the first time,
  // so we don't want to continue to show "No Specs Available" as false positive
  if (!kpfCachedData.itemId) return null;

  const { featuresAttr, url, canonicalUrl, shouldHide } = kpfCachedData;

  if (shouldHide) return null;

  if (!featuresAttr?.length) {
    return responsiveTileGrid ? <NoSpecsAvailable canonicalUrl={canonicalUrl} /> : null;
  }

  if (featuresAttr?.length < minfeatures && !responsiveTileGrid) {
    return null;
  }

  return (
    <KeyProductFeatureContents
      key={kpfCachedData.itemId}
      url={url}
      featuresAttr={featuresAttr}
      oneColumnGrid={oneColumnGrid}
      twoColumnGrid={twoColumnGrid}
      simple={simple}
      forceMobile={forceMobile}
      maxfeatures={maxfeatures}
      displaySimilarLinks={displaySimilarLinks}
      inverseText={inverseText}
      responsiveTileGrid={responsiveTileGrid}
    />
  );
};

KeyProductFeatures.displayName = 'KeyProductFeatures';

KeyProductFeatures.dataModel = dataModel;

KeyProductFeatures.propTypes = {
  displaySimilarLinks: PropTypes.bool,
  hideImage: PropTypes.bool,
  hideForProductTypes: PropTypes.arrayOf(PropTypes.string),
  itemId: PropTypes.string.isRequired,
  maxfeatures: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8]),
  minfeatures: PropTypes.number,
  twoColumnGrid: PropTypes.bool,
  oneColumnGrid: PropTypes.bool,
  simple: PropTypes.bool,
  storeId: PropTypes.string,
  forceMobile: PropTypes.bool,
  firstKPFName: PropTypes.string,
  isConfigurableProduct: PropTypes.bool,
  inverseText: PropTypes.bool,
  responsiveTileGrid: PropTypes.bool
};

KeyProductFeatures.defaultProps = {
  displaySimilarLinks: false,
  hideImage: false,
  hideForProductTypes: [],
  maxfeatures: 8,
  minfeatures: 3,
  twoColumnGrid: false,
  oneColumnGrid: false,
  simple: false,
  storeId: null,
  forceMobile: false,
  firstKPFName: '',
  isConfigurableProduct: false,
  inverseText: false,
  responsiveTileGrid: false
};

export { KeyProductFeatures };
